import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { BrandSelectField } from "@/components/features/brands/BrandSelectField";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { isProOrUltraPlan } from "@/components/shared/PaidPlanRoute";
import { InvoiceEmailAddressListInput } from "@/components/shared/InvoiceEmailAddressListInput";
import { InvoiceEmailCustomBodyEditor } from "@/components/shared/InvoiceEmailCustomBodyEditor";
import { InvoiceEmailPreviewButton } from "@/components/shared/InvoiceEmailPreviewButton";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  buildCreateClientPayload,
  CLIENT_COUNTRY_OPTIONS,
  CLIENT_CURRENCY_OPTIONS,
  CLIENT_DOCUMENT_TYPES,
  clientFormSchema,
  CLIENT_PAYMENT_TERM_OPTIONS,
  defaultClientFormValues,
  type ClientFormValues,
} from "@/lib/client-create-form";
import { clientKeys, tenantKeys } from "@/lib/queryKeys";
import { getDefaultInvoiceEmailCustomHtml } from "@/utils/invoice-email-custom-default-html";
import { htmlVisibleLength } from "@/lib/html-text";
import {
  getDefaultInvoiceEmailSubjectTemplate,
  resolveTenantLangForInvoiceEmail,
} from "@/utils/invoice-email-subject-default";
import { useAuth } from "@/contexts/AuthContext";
import { getTenantCurrent } from "@/services/authService";
import { createClient, getRucInfoForClient } from "@/services/clientService";

export function NewClientPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { user } = useAuth();
  const { data: tenant, isFetched: tenantPlanFetched } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  const canUseInvoiceEmailTemplates =
    tenantPlanFetched && isProOrUltraPlan(tenant?.planId);
  const tenantLang = resolveTenantLangForInvoiceEmail(tenant?.language);

  const createMutation = useMutation({
    mutationFn: createClient,
    onSuccess: () => {
      toast.success(t("clients.new.created", "Client added."));
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
      navigate("/clients");
    },
    onError: () => {
      toast.error(
        t("clients.new.error", "Could not add client. Please try again.")
      );
    },
  });

  const [rucInfoLoading, setRucInfoLoading] = useState(false);
  const [invoiceEmailBodyEditorKey, setInvoiceEmailBodyEditorKey] = useState(0);
  const prefilledFromRucRef = useRef<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ClientFormValues>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: defaultClientFormValues,
  });

  const invoiceEmailRadioId = useId();

  const documentType = watch("documentType");
  const countryCode = watch("countryCode");
  const rutValue = watch("rut");

  const fetchRucInfo = useCallback(() => {
    if (documentType !== "RUT") return;
    const digits = (rutValue ?? "").replace(/\D/g, "");
    if (digits.length !== 12) return;
    const normalized = digits.padStart(12, "0").slice(-12);
    setRucInfoLoading(true);
    getRucInfoForClient(normalized)
      .then((res) => {
        if (!res.valid) {
          toast.error(t("clients.form.rucInfoInvalid", "Could not load company data for this RUT."));
          return;
        }
        prefilledFromRucRef.current = normalized;
        const legalNameTrimmed = res.legalName?.trim();
        if (legalNameTrimmed) {
          setValue("legalName", legalNameTrimmed);
          setValue("displayName", legalNameTrimmed);
        }
        if (res.legalAddress?.trim()) setValue("address", res.legalAddress.trim());
        if (res.city?.trim()) setValue("ciudad", res.city.trim());
        if (res.department?.trim()) setValue("departamento", res.department.trim());
        if (res.email?.trim()) setValue("email", res.email.trim());
        if (res.phone?.trim()) setValue("phone", res.phone.trim());
      })
      .catch(() => {
        toast.error(t("clients.form.rucInfoError", "Could not load company data. Please try again."));
      })
      .finally(() => {
        setRucInfoLoading(false);
      });
  }, [documentType, rutValue, setValue, t]);

  useEffect(() => {
    if (documentType !== "RUT") {
      prefilledFromRucRef.current = null;
    }
  }, [documentType]);

  useEffect(() => {
    const code = countryCode?.trim().toUpperCase();
    if (code && code !== "UY") {
      setValue("documentType", "EXT");
    }
  }, [countryCode, setValue]);

  const onSubmit = (values: ClientFormValues): void => {
    createMutation.mutate(
      buildCreateClientPayload(values, {
        canUseInvoiceEmailTemplates,
      })
    );
  };

  const showRutOrCi = documentType === "RUT" || documentType === "CI";
  const showExt = documentType === "EXT";
  const rutDigits = (rutValue ?? "").replace(/\D/g, "");
  const isPrefilledFromRuc =
    documentType === "RUT" &&
    rutDigits.length === 12 &&
    prefilledFromRucRef.current === rutDigits.padStart(12, "0").slice(-12);

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("clients.new.title", "Add client")}
        description={t(
          "clients.new.description",
          "Add a client to reuse their details when creating invoices."
        )}
        onBack={() => navigate("/clients")}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Card className={rucInfoLoading ? "relative" : undefined}>
          {rucInfoLoading && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/80 backdrop-blur-[1px]"
              aria-live="polite"
              aria-busy="true"
            >
              <div className="flex flex-col items-center gap-3 rounded-md border bg-card px-6 py-4 shadow-sm">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm font-medium text-foreground">
                  {t("clients.form.rucInfoLoading", "Loading company data…")}
                </p>
              </div>
            </div>
          )}
          <CardHeader>
            <CardTitle>
              {t("clients.form.legalInfo", "Legal information")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t("clients.form.documentType", "Document type")} *</Label>
              <Controller
                name="documentType"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_DOCUMENT_TYPES.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {showRutOrCi && (
              <div className="space-y-2">
                <Label htmlFor="rut">
                  {documentType === "RUT"
                    ? t("clients.form.taxId", "Tax ID (RUT)") + " *"
                    : t("clients.form.ci", "Cédula (CI)") + " *"}
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="rut"
                    {...register("rut")}
                    onBlur={fetchRucInfo}
                    placeholder={
                      documentType === "RUT"
                        ? t("clients.form.taxIdPlaceholder", "12 digits")
                        : t("clients.form.ciPlaceholder", "7 or 8 digits")
                    }
                    maxLength={documentType === "RUT" ? 12 : 8}
                    className={errors.rut ? "border-destructive" : ""}
                    disabled={rucInfoLoading}
                    aria-busy={rucInfoLoading}
                  />
                  {rucInfoLoading && (
                    <Loader2
                      className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"
                      aria-hidden
                    />
                  )}
                </div>
                {errors.rut && (
                  <p className="text-sm text-destructive">
                    {errors.rut.message}
                  </p>
                )}
              </div>
            )}

            {showExt && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="countryCode">
                    {t("clients.form.country", "Country")}
                  </Label>
                  <Controller
                    name="countryCode"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value ?? ""}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger id="countryCode">
                          <SelectValue
                            placeholder={t("clients.form.countryPlaceholder")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {CLIENT_COUNTRY_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="docRecepExt">
                    {t("clients.form.docRecepExt", "Document (passport, etc.)")} *
                  </Label>
                  <Input
                    id="docRecepExt"
                    {...register("docRecepExt")}
                    placeholder={t(
                      "clients.form.docRecepExtPlaceholder",
                      "EIN, passport, etc. (max 20)"
                    )}
                    maxLength={20}
                    className={errors.docRecepExt ? "border-destructive" : ""}
                  />
                  {errors.docRecepExt && (
                    <p className="text-sm text-destructive">
                      {errors.docRecepExt.message}
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="legalName">
                {t("clients.form.legalName", "Razón social")} *
              </Label>
              <Input
                id="legalName"
                {...register("legalName")}
                placeholder={t(
                  "clients.form.legalNamePlaceholder",
                  "Legal name (always saved in uppercase)"
                )}
                className={errors.legalName ? "border-destructive" : ""}
                disabled={isPrefilledFromRuc}
                readOnly={isPrefilledFromRuc}
              />
              {errors.legalName && (
                <p className="text-sm text-destructive">
                  {errors.legalName.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayName">
                {t("clients.form.displayName", "Display / fantasy name")}
              </Label>
              <Input
                id="displayName"
                {...register("displayName")}
                placeholder={t(
                  "clients.form.displayNamePlaceholder",
                  "Optional – name the company uses publicly"
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">
                {t("clients.form.address", "Address")}
                {documentType === "RUT" ? " *" : null}
              </Label>
              <Input
                id="address"
                className={errors.address ? "border-destructive" : ""}
                {...register("address")}
                placeholder={
                  documentType === "RUT"
                    ? t("clients.form.addressPlaceholderLegal", "Legal business address")
                    : t("clients.form.addressPlaceholder", "Optional")
                }
                disabled={isPrefilledFromRuc}
                readOnly={isPrefilledFromRuc}
              />
              {errors.address && (
                <p className="text-sm text-destructive">
                  {errors.address.message}
                </p>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ciudad">
                  {t("clients.form.ciudad", "City")} (
                  {t("clients.form.optionalTag", "optional")})
                </Label>
                <Input
                  id="ciudad"
                  {...register("ciudad")}
                  maxLength={30}
                  placeholder={t("clients.form.ciudadPlaceholder", "Montevideo")}
                  disabled={isPrefilledFromRuc}
                  readOnly={isPrefilledFromRuc}
                  className="transition-colors duration-150"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="departamento">
                  {t("clients.form.departamento", "Department / state")} (
                  {t("clients.form.optionalTag", "optional")})
                </Label>
                <Input
                  id="departamento"
                  {...register("departamento")}
                  maxLength={30}
                  placeholder={t(
                    "clients.form.departamentoPlaceholder",
                    "Montevideo"
                  )}
                  disabled={isPrefilledFromRuc}
                  readOnly={isPrefilledFromRuc}
                  className="transition-colors duration-150"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {t("clients.form.contactInfo", "Contact information")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">
                {t("clients.form.email", "Email")}
              </Label>
              <Input
                id="email"
                type="email"
                {...register("email")}
                placeholder={t("clients.form.emailPlaceholder", "client@example.com")}
                className={errors.email ? "border-destructive" : ""}
              />
              {errors.email && (
                <p className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">
                {t("clients.form.phone", "Phone")}
              </Label>
              <Input
                id="phone"
                {...register("phone")}
                placeholder={t("clients.form.phonePlaceholder", "Optional")}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {t("clients.form.invoicingDetails", "Data for invoicing")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Renders nothing when the tenant has no marcas. Same component
                as the edit page, so the two cannot describe it differently. */}
            <Controller
              control={control}
              name="defaultBrandId"
              render={({ field }) => (
                <BrandSelectField
                  value={field.value ?? ""}
                  onChange={field.onChange}
                />
              )}
            />
            <div className="space-y-2">
              <Label>{t("clients.form.defaultCurrency", "Default currency")}</Label>
              <Controller
                name="defaultCurrency"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ?? "UYU"}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_CURRENCY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {t("clients.form.paymentType", "Forma de pago (plazos)")}
              </Label>
              <Controller
                name="paymentTermDays"
                control={control}
                render={({ field }) => (
                  <Select
                    value={String(field.value ?? 0)}
                    onValueChange={(v) => field.onChange(Number(v) as 0 | 30 | 60 | 90)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_PAYMENT_TERM_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="discountPercent">
                {t("clients.form.discountPercent", "Discount (%)")}
              </Label>
              <Input
                id="discountPercent"
                type="number"
                min={0}
                max={100}
                step={0.5}
                placeholder={t(
                  "clients.form.discountPercentPlaceholder",
                  "Optional"
                )}
                {...register("discountPercent")}
                className={errors.discountPercent ? "border-destructive" : ""}
              />
              {errors.discountPercent && (
                <p className="text-sm text-destructive">
                  {errors.discountPercent.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">
              {t("clients.form.invoiceEmailCardTitle")}
            </CardTitle>
            <CardDescription>
              {t("clients.form.invoiceEmailCardDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start space-x-2 space-y-0 rounded-md border p-4 transition-colors duration-150">
              <Controller
                name="sendInvoiceByEmail"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="sendInvoiceByEmail"
                    checked={field.value ?? false}
                    onCheckedChange={(checked) =>
                      field.onChange(checked === true)
                    }
                  />
                )}
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="sendInvoiceByEmail"
                  className="cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t("clients.form.sendInvoiceByEmail", "Send invoice by email")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "clients.form.sendInvoiceByEmailHelp",
                    "Uses the email from contact information."
                  )}
                </p>
              </div>
            </div>
            {watch("sendInvoiceByEmail") === true && (
              <div className="space-y-4">
                <div className="flex items-start space-x-2 space-y-0 rounded-md border p-4 transition-colors duration-150">
                  <Controller
                    name="autoSendInvoiceEmail"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="autoSendInvoiceEmail"
                        checked={field.value ?? false}
                        onCheckedChange={(checked) =>
                          field.onChange(checked === true)
                        }
                      />
                    )}
                  />
                  <div className="grid gap-1.5 leading-none">
                    <Label
                      htmlFor="autoSendInvoiceEmail"
                      className="cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {t("clients.form.autoSendInvoiceEmail", "Send automatically when emitted")}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "clients.form.autoSendInvoiceEmailHelp",
                        "Sent as soon as DGI confirms the invoice. Turn off to send manually from each invoice instead."
                      )}
                    </p>
                  </div>
                </div>
                <Label>{t("clients.form.invoiceEmailLabel", "Email for invoice")}</Label>
                <Controller
                  name="invoiceEmailChoice"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value ?? "contact"}
                      onValueChange={(v) => {
                        field.onChange(v);
                        if (v === "contact") {
                          setValue("invoiceEmailCc", []);
                        }
                      }}
                      className="flex flex-col gap-2"
                      aria-label={t("clients.form.invoiceEmailLabel", "Email for invoice")}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="contact"
                          id={`${invoiceEmailRadioId}-contact`}
                        />
                        <Label
                          htmlFor={`${invoiceEmailRadioId}-contact`}
                          className="cursor-pointer font-normal"
                        >
                          {t("clients.form.useContactEmail", "Use contact email")}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="different"
                          id={`${invoiceEmailRadioId}-different`}
                        />
                        <Label
                          htmlFor={`${invoiceEmailRadioId}-different`}
                          className="cursor-pointer font-normal"
                        >
                          {t("clients.form.useDifferentEmail", "Use a different email")}
                        </Label>
                      </div>
                    </RadioGroup>
                  )}
                />
                {watch("invoiceEmailChoice") === "different" && (
                  <div className="space-y-4 pl-6">
                    <div className="space-y-2">
                      <Controller
                        name="invoiceEmail"
                        control={control}
                        render={({ field }) => (
                          <InvoiceEmailAddressListInput
                            id="invoiceEmail"
                            value={field.value ?? []}
                            onChange={field.onChange}
                            placeholder={t(
                              "clients.form.invoiceEmailPlaceholder",
                              "invoice@example.com"
                            )}
                            aria-invalid={Boolean(errors.invoiceEmail)}
                          />
                        )}
                      />
                      {errors.invoiceEmail && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.invoiceEmail.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invoiceEmailCc-new">{t("clients.form.invoiceEmailCcLabel")}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t("clients.form.invoiceEmailCcHelp")}
                      </p>
                      <Controller
                        name="invoiceEmailCc"
                        control={control}
                        render={({ field }) => (
                          <InvoiceEmailAddressListInput
                            id="invoiceEmailCc-new"
                            value={field.value ?? []}
                            onChange={field.onChange}
                            placeholder={t("clients.form.invoiceEmailCcPlaceholder")}
                            aria-invalid={Boolean(errors.invoiceEmailCc)}
                          />
                        )}
                      />
                      {errors.invoiceEmailCc && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.invoiceEmailCc.message}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <div className="space-y-2 pt-1">
                  <Label htmlFor="invoiceEmailTemplate">
                    {t("clients.form.invoiceEmailTemplateLabel", "Email template")}
                  </Label>
                  <Controller
                    name="invoiceEmailTemplate"
                    control={control}
                    render={({ field }) => {
                      const raw = field.value ?? "default";
                      const selectValue =
                        raw === "custom" && !canUseInvoiceEmailTemplates
                          ? "default"
                          : raw;
                      return (
                        <Select
                          value={selectValue}
                          onValueChange={(v) => {
                            if (v === "custom" && !canUseInvoiceEmailTemplates) {
                              return;
                            }
                            if (v === "custom" && canUseInvoiceEmailTemplates) {
                              const currentLength = htmlVisibleLength(
                                getValues("invoiceEmailCustomBody")
                              );
                              if (currentLength === 0) {
                                setValue(
                                  "invoiceEmailCustomBody",
                                  getDefaultInvoiceEmailCustomHtml(t)
                                );
                                setInvoiceEmailBodyEditorKey((k) => k + 1);
                              }
                              const currentSubject =
                                getValues("invoiceEmailCustomSubject")?.trim() ?? "";
                              if (currentSubject.length === 0) {
                                setValue(
                                  "invoiceEmailCustomSubject",
                                  getDefaultInvoiceEmailSubjectTemplate(tenantLang)
                                );
                              }
                            }
                            field.onChange(v);
                          }}
                        >
                          <SelectTrigger
                            id="invoiceEmailTemplate"
                            className="transition-colors duration-150"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">
                              {t("clients.form.invoiceEmailTemplateDefault")}
                            </SelectItem>
                            <SelectItem
                              value="custom"
                              disabled={!canUseInvoiceEmailTemplates}
                              className={
                                !canUseInvoiceEmailTemplates ? "opacity-70" : undefined
                              }
                            >
                              <span>{t("clients.form.invoiceEmailTemplateCustom")}</span>
                              {!canUseInvoiceEmailTemplates ? (
                                <span className="text-muted-foreground">
                                  {" "}
                                  {t("clients.form.invoiceEmailTemplateCustomProOnly")}
                                </span>
                              ) : null}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      );
                    }}
                  />
                </div>
                {canUseInvoiceEmailTemplates &&
                  watch("invoiceEmailTemplate") === "custom" && (
                    <div className="space-y-4">
                      <div className="flex items-start space-x-2 space-y-0 rounded-md border p-4 transition-colors duration-150">
                        <Controller
                          name="invoiceEmailIncludeCompanyLogo"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              id="invoiceEmailIncludeCompanyLogo"
                              checked={field.value ?? true}
                              onCheckedChange={(checked) =>
                                field.onChange(checked === true)
                              }
                              className="transition-colors duration-150"
                            />
                          )}
                        />
                        <div className="grid gap-1.5 leading-none">
                          <Label
                            htmlFor="invoiceEmailIncludeCompanyLogo"
                            className="cursor-pointer text-sm font-medium leading-none text-foreground"
                          >
                            {t("clients.form.invoiceEmailIncludeCompanyLogo")}
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            {t("clients.form.invoiceEmailIncludeCompanyLogoHelp")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2 space-y-0 rounded-md border p-4 transition-colors duration-150">
                        <Controller
                          name="invoiceEmailIncludeInvoiceDetails"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              id="invoiceEmailIncludeInvoiceDetails"
                              checked={field.value ?? true}
                              onCheckedChange={(checked) =>
                                field.onChange(checked === true)
                              }
                              className="transition-colors duration-150"
                            />
                          )}
                        />
                        <div className="grid gap-1.5 leading-none">
                          <Label
                            htmlFor="invoiceEmailIncludeInvoiceDetails"
                            className="cursor-pointer text-sm font-medium leading-none text-foreground"
                          >
                            {t("clients.form.invoiceEmailIncludeInvoiceDetails")}
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            {t("clients.form.invoiceEmailIncludeInvoiceDetailsHelp")}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="invoiceEmailCustomSubject-new">
                          {t("clients.form.invoiceEmailCustomSubjectLabel", "Email subject")}
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          {t("clients.form.invoiceEmailCustomSubjectHelp")}
                        </p>
                        <Input
                          id="invoiceEmailCustomSubject-new"
                          {...register("invoiceEmailCustomSubject")}
                          maxLength={255}
                          className="transition-colors duration-150"
                          aria-invalid={Boolean(errors.invoiceEmailCustomSubject)}
                          autoComplete="off"
                        />
                        <div className="flex justify-end">
                          <p className="text-xs text-muted-foreground tabular-nums">
                            {(watch("invoiceEmailCustomSubject") ?? "").length} / 255
                          </p>
                        </div>
                        {errors.invoiceEmailCustomSubject && (
                          <p className="text-sm text-destructive">
                            {errors.invoiceEmailCustomSubject.message}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="invoiceEmailCustomBody">
                          {t("clients.form.invoiceEmailCustomBodyLabel", "Custom message")}
                        </Label>
                        <Controller
                          name="invoiceEmailCustomBody"
                          control={control}
                          render={({ field }) => (
                            <InvoiceEmailCustomBodyEditor
                              key={invoiceEmailBodyEditorKey}
                              initialHtml={field.value ?? ""}
                              onHtmlChange={field.onChange}
                              onBlur={field.onBlur}
                              disabled={createMutation.isPending}
                              hasError={Boolean(errors.invoiceEmailCustomBody)}
                              placeholder={t(
                                "clients.form.invoiceEmailCustomBodyPlaceholder"
                              )}
                              toolbarAriaLabel={t(
                                "clients.form.invoiceEmailCustomBodyToolbarAria"
                              )}
                              editorId="invoiceEmailCustomBody"
                            />
                          )}
                        />
                        <div className="flex justify-end">
                          <p className="text-xs text-muted-foreground tabular-nums">
                            {htmlVisibleLength(watch("invoiceEmailCustomBody"))} / 2000
                          </p>
                        </div>
                        {errors.invoiceEmailCustomBody && (
                          <p className="text-sm text-destructive">
                            {errors.invoiceEmailCustomBody.message}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            )}
            {watch("sendInvoiceByEmail") === true && canUseInvoiceEmailTemplates ? (
              <div className="flex justify-end border-t border-border pt-4">
                <InvoiceEmailPreviewButton
                  getPayload={() => {
                    const template = watch("invoiceEmailTemplate") ?? "default";
                    if (template === "custom") {
                      return {
                        invoiceEmailTemplate: "custom",
                        invoiceEmailCustomBody:
                          watch("invoiceEmailCustomBody") ?? undefined,
                        invoiceEmailIncludeCompanyLogo:
                          watch("invoiceEmailIncludeCompanyLogo") ?? true,
                        invoiceEmailIncludeInvoiceDetails:
                          watch("invoiceEmailIncludeInvoiceDetails") ?? true,
                      };
                    }
                    return {
                      invoiceEmailTemplate: template,
                      invoiceEmailCustomBody: undefined,
                    };
                  }}
                  disabled={createMutation.isPending}
                />
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-row justify-between pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/clients")}
            >
              {t("clients.form.cancel", "Cancel")}
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending
                ? t("clients.form.saving", "Saving…")
                : t("clients.form.save", "Save")}
            </Button>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
